import { CourseTable, RowOptions } from "./tableTypes";

export interface LinkItem {
  label: string;
  href: string;
}

export interface ResourceCategory {
  title: string;
  links: LinkItem[];
}

export interface ContactLink {
  label: string;
  address: string;
  description: string;
}

export type HoursMode = "project" | "conceptual-debugging";

export interface SrcTopic {
  title: string;
  lectures: number[];
}

export interface CourseContent {
  lecturesTable: CourseTable;
  assignmentsTable: CourseTable;
  labsTable: CourseTable;
  codeAlongsTable: CourseTable;
  hoursMode: HoursMode;
  projectHoursCalendar: string;
  conceptualHoursCalendar: string;
  debuggingHoursCalendar: string;
  quickLinks: LinkItem[];
  resourceCategories: ResourceCategory[];
  contactLinks: {
    ta: ContactLink;
    hta: ContactLink;
    individual: ContactLink;
  };
  srcTopics: SrcTopic[];
}

interface JsonTableRow {
  cells: string[];
  links?: Record<string, string>;
  release?: string;
  due?: string;
}

interface JsonTable {
  header: string[];
  rows: JsonTableRow[];
}

interface CourseJson {
  lectures: JsonTable;
  assignments: JsonTable;
  labs: JsonTable;
  codeAlongs: JsonTable;
  hours: {
    mode: HoursMode;
    projectCalendar: string;
    conceptualCalendar: string;
    debuggingCalendar: string;
  };
  resources: {
    quickLinks: LinkItem[];
    categories: ResourceCategory[];
    contact: CourseContent["contactLinks"];
  };
  srcTopics: SrcTopic[];
}

const CONTENT_URL = `${process.env.PUBLIC_URL}/content/course.json`;

let loaded: CourseContent | null = null;

function tableFromJson(raw: JsonTable): CourseTable {
  return {
    header: raw.header,
    rows: raw.rows.map(
      (row): RowOptions => ({
        cellNames: row.cells,
        cellNametoLinks: new Map(
          Object.entries(row.links ?? {}).filter(([, href]) => Boolean(href))
        ),
        autoReleaseDate: row.release || undefined,
        dueDate: row.due || undefined,
      })
    ),
  };
}

function parseCourseJson(raw: CourseJson): CourseContent {
  return {
    lecturesTable: tableFromJson(raw.lectures),
    assignmentsTable: tableFromJson(raw.assignments),
    labsTable: tableFromJson(raw.labs),
    codeAlongsTable: tableFromJson(raw.codeAlongs),
    hoursMode: raw.hours.mode,
    projectHoursCalendar: raw.hours.projectCalendar,
    conceptualHoursCalendar: raw.hours.conceptualCalendar,
    debuggingHoursCalendar: raw.hours.debuggingCalendar,
    quickLinks: raw.resources.quickLinks,
    resourceCategories: raw.resources.categories,
    contactLinks: raw.resources.contact,
    srcTopics: raw.srcTopics ?? [],
  };
}

/** Fetch `public/content/course.json` (same file HTAs edit on the course www tree). */
export async function loadCourseContent(): Promise<CourseContent> {
  const response = await fetch(CONTENT_URL, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to load course content (${response.status})`);
  }
  const json = (await response.json()) as CourseJson;
  loaded = parseCourseJson(json);
  return loaded;
}

export function getCourseContent(): CourseContent {
  if (!loaded) {
    throw new Error("Course content has not loaded yet");
  }
  return loaded;
}
